/**
 * Field extractor for Static Web Generator
 * Uses fieldMapping.ts to extract values from component state
 */

import { WebsiteGeneratorState } from "../../../types/staticWebGenerator";
import { SCHEMA_TO_COMPONENT_MAPPING } from "./fieldMapping";
import { getPhotoUrl, getHeroImageUrl } from "./photoUtils";
import { getStateValue } from "./valueUtils";
import { getArrayBuilder } from "./sectionBuilders";
import { generateBookingLink } from "../../../utils/bookingLinkUtils";

/**
 * Special field handlers for fields that need custom logic
 * Key: "schemaSection.schemaField"
 */
type FieldHandler = (state: WebsiteGeneratorState, businessInfo: any) => any;

const SPECIAL_FIELD_HANDLERS: Record<string, FieldHandler> = {
  // Hero image - uses selectedHeroPhoto with fallback to hero.photos[0]
  "hero_section.image": (state) => {
    return getHeroImageUrl(state.selectedHeroPhoto, state.hero?.photos);
  },

  // Include book now button - boolean with default false
  "hero_section.include_book_now_button": (state) => {
    return state.hero?.includeBookingButton ?? false;
  },

  // Book link copy - only include if button is enabled
  "hero_section.book_link_copy": (state) => {
    if (state.hero?.includeBookingButton) {
      return getStateValue(state.hero.heroBookingCopy);
    }
    return undefined;
  },

  // Booking link URL - with default generation
  "booking_link.url": (state, businessInfo) => {
    const value = getStateValue(state.bookingLink?.link);
    if (value !== undefined) return value;
    // Generate default booking link
    if (businessInfo?.id) {
      return generateBookingLink(businessInfo.id);
    }
    return undefined;
  },

  // Booking link title - priority: bookingLink.copy → hero.heroBookingCopy
  "booking_link.title": (state) => {
    const bookingLinkCopy = getStateValue(state.bookingLink?.copy);
    if (bookingLinkCopy !== undefined) return bookingLinkCopy;
    if (state.hero?.includeBookingButton) {
      return getStateValue(state.hero.heroBookingCopy);
    }
    return undefined;
  },

  // Google maps - boolean from contact.showMap
  "find_us.google_maps": (state) => {
    return state.contact?.showMap ?? false;
  },
};

/**
 * Get component section data from state
 */
const getComponentSectionData = (
  componentSection: string,
  state: WebsiteGeneratorState,
): any => {
  const sectionMap: Record<string, any> = {
    hero: state.hero,
    services: state.services,
    team: state.team,
    contact: state.contact,
    footer: state.footer,
    bookingLink: state.bookingLink,
    colors: state.colors,
    fonts: state.fonts,
    gallery: state.gallery,
    testimonials: state.testimonials,
    announcementBanner: state.announcementBanner,
    banner: state.banner,
    highlights: state.highlights,
  };
  return sectionMap[componentSection];
};

/**
 * Extract value from state for a given schema section and field
 * Uses fieldMapping.ts for simple mappings, special handlers for complex cases
 */
export const extractFieldValue = (
  schemaSection: string,
  schemaField: string,
  state: WebsiteGeneratorState,
  businessInfo: any,
): any => {
  const schemaPath = `${schemaSection}.${schemaField}`;

  // Check for special handler first
  const specialHandler = SPECIAL_FIELD_HANDLERS[schemaPath];
  if (specialHandler) {
    return specialHandler(state, businessInfo);
  }

  // Check for array builder (gallery items, testimonials, etc.)
  const arrayBuilder = getArrayBuilder(schemaSection, schemaField);
  if (arrayBuilder) {
    return arrayBuilder(state);
  }

  // Use fieldMapping.ts for simple field mappings
  const mapping = SCHEMA_TO_COMPONENT_MAPPING.find(
    (m) => m.schemaPath === schemaPath,
  );

  if (mapping) {
    const sectionData = getComponentSectionData(
      mapping.componentSection,
      state,
    );
    if (sectionData && mapping.componentField in sectionData) {
      return getStateValue(sectionData[mapping.componentField]);
    }
  }

  return undefined;
};

/**
 * Check if a section is enabled based on optionalSectionEnabled or state
 */
export const isSectionEnabled = (
  schemaKey: string,
  state: WebsiteGeneratorState,
  optionalSectionEnabled?: Record<string, boolean>,
): boolean => {
  if (optionalSectionEnabled) {
    // Map schema key to enabled key
    const enabledKeyMap: Record<string, string> = {
      service_highlights: "highlights",
      announcement_modal: "announcementBanner",
    };
    const enabledKey = enabledKeyMap[schemaKey] || schemaKey;
    return optionalSectionEnabled[enabledKey] === true;
  }

  // Fallback to state check
  const stateKeyMap: Record<string, keyof WebsiteGeneratorState> = {
    gallery: "gallery",
    testimonials: "testimonials",
    service_highlights: "highlights",
    banner: "banner",
    announcement_modal: "announcementBanner",
    team: "team",
  };

  const stateKey = stateKeyMap[schemaKey];
  if (stateKey) {
    return state[stateKey] !== null && state[stateKey] !== undefined;
  }

  return false;
};
